import { motion } from 'framer-motion';
import { Instagram, Facebook, Youtube, Twitter, Linkedin, Send } from 'lucide-react';
import { useState } from 'react';
import { BRAND, NAV_LINKS } from '@/lib/site';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function Footer() {
  const [email, setEmail] = useState('');
  const onNewsletter = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    toast.success('Subscribed. Welcome to the family.');
    setEmail('');
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative pt-20 pb-10 bg-gradient-to-b from-[#2A1820] via-[#22141A] to-[#1A0F14] text-saffron-100/85 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-mandala bg-center bg-no-repeat opacity-[0.05] animate-slow-spin" />
      <div className="absolute inset-x-0 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-saffron-500/40 to-transparent" />

      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 flex-none place-items-center rounded-xl bg-cream/95 p-1 shadow-[0_8px_24px_-8px_rgba(255,103,31,0.5)]">
                <img src={BRAND.logo} alt="" className="h-full w-full object-contain mix-blend-multiply" />
              </div>
              <div>
                <p className="font-display text-xl font-bold text-saffron-100">ADI FOUNDATION</p>
                <p className="font-devanagari text-saffron-300 text-sm">{BRAND.taglineHi}</p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-saffron-100/65">
              ADI FOUNDATION is a pan-India non-profit dedicated to food, education, healthcare,
              women empowerment, environmental protection, rural development, animal welfare and
              disaster response.
            </p>

            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: Instagram, href: BRAND.social.instagram, l: 'Instagram' },
                { icon: Facebook, href: BRAND.social.facebook, l: 'Facebook' },
                { icon: Twitter, href: BRAND.social.twitter, l: 'X (Twitter)' },
                { icon: Youtube, href: BRAND.social.youtube, l: 'YouTube' },
                { icon: Linkedin, href: BRAND.social.linkedin, l: 'LinkedIn' },
              ].map((s) => (
                <a
                  key={s.l}
                  href={s.href}
                  aria-label={s.l}
                  data-testid={`social-${s.l.toLowerCase().split(' ')[0]}`}
                  className="grid h-9 w-9 place-items-center rounded-full border border-saffron-500/25 hover:bg-saffron-500 hover:text-white transition"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-saffron-300">
              Explore
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-y-2.5 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-saffron-100/75 hover:text-saffron-300 transition"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-saffron-300">
              Contact
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li>
                <a
                  href={BRAND.phoneHref}
                  className="hover:text-saffron-300 transition"
                  data-testid="footer-phone"
                >
                  {BRAND.phone}
                </a>
              </li>
              <li>
                <a
                  href={BRAND.emailHref}
                  className="hover:text-saffron-300 transition break-all"
                  data-testid="footer-email"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="text-saffron-100/70 mt-3">Operating Across India</li>
              <li className="text-xs text-saffron-100/55">Delhi · Lucknow · Nagpur</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-saffron-300">
              Newsletter
            </p>
            <p className="mt-5 text-sm text-saffron-100/70">
              A monthly note from the field. Real photos. Real impact. No spam.
            </p>
            <form
              onSubmit={onNewsletter}
              data-testid="newsletter-form"
              className="mt-4 flex w-full items-center gap-2 rounded-full border border-saffron-500/30 bg-white/5 p-1.5 backdrop-blur"
            >
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-saffron-100 placeholder:text-saffron-100/40 focus-visible:ring-0 shadow-none"
              />
              <Button
                type="submit"
                data-testid="newsletter-submit"
                size="icon"
                aria-label="Subscribe"
                className="h-9 w-9 flex-none rounded-full bg-saffron-500 hover:bg-saffron-600 text-white"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-14 border-t border-saffron-500/15 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-saffron-100/55"
        >
          <p data-testid="footer-copyright">
            © 2026 ADI FOUNDATION. All Rights Reserved.
          </p>
          <p className="font-serif italic text-saffron-200/70">
            Built with seva. Deployed with dignity.
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
